import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  ButtonProps,
  HStack,
  PopoverContent,
  Popover,
  PopoverTrigger,
  Button,
  PopoverBody,
  Text,
  TagProps,
  Tag,
  PlacementWithLogical,
  PopoverProps,
  useOutsideClick,
  Portal,
} from "@chakra-ui/react";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Except } from "type-fest";
import { formatNumberLocal } from "../../helpers/formatNumberLocal";
import { useCloseWhenOutsideViewport } from "../../hooks/useCloseWhenOutsideViewport";
import { ImageWithMissingSrc } from "../atoms/ImageWithMissingSrc";
import { useOverlayPortalContainer } from "../modals/OverlayPortalContainerContext";
import { DefaultOptionContent } from "./parts/OptionItem";
import {
  StandardSelectOption,
  StandardSelectOptionData,
} from "./parts/SelectOption";
import { SelectorContainer } from "./parts/SelectorContainer";
import { useSearchOptions } from "./parts/useSearchOptions";

export interface SelectorProps<T extends string> {
  options: StandardSelectOption<T>[];
  values: T[]; // list of options currently selected, equal to the 'value' field on the SelectOption.

  // callbacks
  onChange: (values: T[]) => void;
  onOpen?: () => void;

  // config
  enableDoubleColumn?: boolean;
  enableSearch?: boolean;
  enableBulkSelect?: boolean; // show the select all and clear all buttons in the footer
  moveSelectedToTop?: boolean; // selected options sort to the top of the list
  enableMultiSelect?: boolean;
  enableDeselect?: boolean;
  clearInvalidValues?: boolean; // automatically deselect values which don't appear in the supplied options
  autoSelectSingleItem?: boolean; // automatically select if there is only a single option
  maxOptionsDisplayed?: number; // cut off the number of options in the dropdown

  // state
  isDisabled?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean; // for forms, must be provided even when in a FormControl
  isRequired?: boolean; // for forms

  // styling
  placement?: PlacementWithLogical;
  placeholder?: string; // placeholder text to show in the selector button when nothing is selected.
  size?: ButtonProps["size"] & TagProps["size"];
  expandIcon?: React.ReactElement; // provide a different toggle icon, default is a chevron down
  wrapText?: boolean; // allow text to wrap within the option item, otherwise it will crop to a single line

  popoverProps?: Omit<PopoverProps, "children" | "width">;
  buttonProps?: Except<ButtonProps, "onClick" | "children" | "size">;

  initialOpenState?: boolean;
}

/**
 * Configure a dropdown selector for selecting one or more options from a list.
 */
export function Selector<T extends string>({
  options,
  values,
  onChange,
  onOpen,
  moveSelectedToTop = false,
  enableSearch = false,
  enableBulkSelect = false,
  enableDoubleColumn = false,
  enableMultiSelect = false,
  enableDeselect = false,
  clearInvalidValues = true,
  isInvalid = false,
  isDisabled = false,
  isLoading = false,
  isRequired = false,
  autoSelectSingleItem = false,
  maxOptionsDisplayed,
  expandIcon = <ChevronDownIcon />,
  placement = "bottom-start",
  placeholder = "Select",
  size,
  popoverProps,
  buttonProps,
  wrapText = false,
  initialOpenState = false,
}: SelectorProps<T>) {
  const [isOpen, setIsOpen] = useState(initialOpenState);

  const optionByValue = useMemo(() => {
    return new Map(options.map((option) => [option.value, option]));
  }, [options]);

  const firstSelectedOption = useMemo(() => {
    // only used for single-select mode
    if (enableMultiSelect) {
      return undefined;
    }

    if (values.length === 0) {
      return undefined;
    }

    return optionByValue.get(values[0]);
  }, [values, optionByValue, enableMultiSelect]);

  useClearInvalidValues({
    enabled: clearInvalidValues,
    values,
    optionByValue,
    onChange,
  });

  // for the only option to be auto-selected for the user
  useEffect(() => {
    if (
      autoSelectSingleItem &&
      !enableDeselect &&
      options.length === 1 &&
      values.length === 0
    ) {
      onChange([options[0].value]);
    }
  }, [autoSelectSingleItem, enableDeselect, onChange, options, values.length]);

  const hasSelection = values.length > 0;

  // search input is controlled by the selector, as we allow the user
  // to type into the button (due to keyboard navigation) to trigger the
  // dropdown to open with the initial input char.
  const { searchValue, setSearchValue, searchedOptions } = useSearchOptions({
    options,
    enabled: enableSearch && isOpen, // don't build fuse search until it's actually opened
    maxOptionsDisplayed,
  });

  // to close the dropdown, we toggle the state and clear the search field.
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchValue("");
  }, [setSearchValue]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // the default popover implementation doesn't always close even when it lost
  // focus, so we manage ourselves.
  useOutsideClick({
    ref: contentRef,
    handler: (event) => {
      const target = event.target as Node | null;

      // a click into the trigger button should still not close the popover
      if (triggerRef.current?.contains(target)) {
        return;
      }

      handleClose();
    },
  });

  const portalContainerRef = useOverlayPortalContainer();

  useCloseWhenOutsideViewport({
    isOpen,
    targetRef: triggerRef,
    onClose: handleClose,
  });

  const handleOnChange = useCallback(
    (values) => {
      onChange(values);
      if (!enableMultiSelect) {
        handleClose(); // close on single select
      }
    },
    [onChange, enableMultiSelect, handleClose],
  );

  return (
    <Popover
      placement={placement}
      autoFocus={false}
      isOpen={isOpen}
      onOpen={() => handleOpen()}
      onClose={() => {
        handleClose();
      }}
      isLazy
      modifiers={[
        // There is a child component called 'chakra-popover__popper' which
        // can't be styled directly. For some reason, it receives a min-width
        // prop which gets set to max-content. So even if you supply
        // matchWidth: true, the min-width will still force the popover content
        // width to grow to the max-content. We solve the issue by overriding
        // the min-width prop.
        {
          name: "removeMinWidth",
          enabled: popoverProps?.matchWidth,
          phase: "beforeWrite",
          fn: ({ state }) => {
            state.styles.popper.minWidth = "0";
          },
        },
      ]}
      {...popoverProps}
    >
      <PopoverTrigger>
        <Button
          ref={triggerRef}
          variant="outline"
          size={size}
          onClick={() => {
            if (isOpen) {
              handleClose();
            } else {
              handleOpen();
            }
          }}
          isDisabled={isDisabled}
          justifyContent="space-between"
          rightIcon={expandIcon}
          borderColor={
            // style the valid selected state as this isn't done in the theme
            !isInvalid && hasSelection ? "gray.500" : undefined
          }
          fontWeight={hasSelection ? "medium" : "normal"}
          color={hasSelection ? "gray.700" : "gray.500"}
          aria-required={isRequired}
          aria-invalid={isInvalid}
          onKeyDown={(e) => {
            if (isDisabled) {
              return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              e.stopPropagation();

              if (!isOpen) {
                handleOpen();
              }
            }

            // allow the user to type into the button to open the
            // popover and copy the initial character into the search
            const isPrintableCharacter =
              e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

            if (enableSearch && isPrintableCharacter) {
              e.preventDefault();
              e.stopPropagation();

              if (!isOpen) {
                setSearchValue(e.key);
                handleOpen();
              }
            }
          }}
          onBlur={(e) => {
            // close if the user clicks outside the popover, or if they
            // navigate away using the keyboard
            if (
              isOpen &&
              !triggerRef.current?.contains(e.relatedTarget) &&
              !contentRef.current?.contains(e.relatedTarget)
            ) {
              handleClose();
            }
          }}
          {...buttonProps}
        >
          {enableMultiSelect ?
            <MultiSelectButtonContent
              selectedCount={values.length}
              placeholder={placeholder}
              size={size}
            />
          : <SingleSelectButtonContent
              selectedOption={firstSelectedOption}
              isLoading={isLoading}
              placeholder={placeholder}
            />
          }
        </Button>
      </PopoverTrigger>
      <Portal containerRef={portalContainerRef}>
        <PopoverContent
          ref={contentRef}
          role="listbox"
          zIndex="dropdown"
          backgroundColor="white"
          maxH="min(30rem, calc(50dvh - 1rem))" // take a bit off due to the button height itself
          {...(popoverProps?.matchWidth ?
            {
              width: "full", // allow the content to grow to the full matched-width of the button
            }
          : {
              maxW: "30rem",
              minW: 0,
              width: "auto", // somehow getting a default xs width, so we override
            })}
          overflow="hidden"
        >
          <PopoverBody
            display="flex"
            flexGrow={1}
            flexShrink={1}
            minH={0}
            minW={0}
            p={0}
          >
            <SelectorContainer
              options={options}
              selectedValues={values}
              searchOptions={searchedOptions}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              enableSearch={enableSearch}
              enableMultiSelect={enableMultiSelect}
              enableDoubleColumn={enableDoubleColumn}
              enableBulkSelect={enableBulkSelect}
              moveSelectedToTop={moveSelectedToTop}
              enableDeselect={enableDeselect}
              isLoading={isLoading}
              onChange={handleOnChange}
              wrapText={wrapText}
              OptionContent={DefaultOptionContent}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

// default content in the button for single-select mode, will show the selected
// option icon and text
export function SingleSelectButtonContent<TValue extends string>({
  selectedOption,
  isLoading,
  placeholder,
}: {
  selectedOption: StandardSelectOption<TValue> | undefined;
  isLoading?: boolean;
  placeholder?: string;
}) {
  return (
    <HStack w="full" h="full" justify="flex-start" overflow="hidden">
      {selectedOption && <SelectButtonIcon data={selectedOption.data} />}
      <Text noOfLines={1} wordBreak="break-all" minW={0}>
        {isLoading ? "Loading..." : (selectedOption?.data.label ?? placeholder)}
      </Text>
    </HStack>
  );
}

function SelectButtonIcon({ data }: { data: StandardSelectOptionData }) {
  if (!("icon" in data)) {
    return null;
  }

  // we still call this even when the icon field is present but undefined
  if (!data.icon || typeof data.icon === "string") {
    return (
      <ImageWithMissingSrc
        src={data.icon}
        alt=""
        objectFit="contain"
        h="full"
        flexShrink={0}
        py={1}
      />
    );
  }

  return data.icon;
}

// default content in the button for multi-select mode, will show the
// number of selected items in a tag
function MultiSelectButtonContent({
  placeholder,
  selectedCount,
  size,
}: {
  placeholder: string;
  selectedCount: number;
  size?: TagProps["size"];
}) {
  return (
    <HStack>
      <Text
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        noOfLines={1}
      >
        {placeholder}
      </Text>
      {selectedCount > 0 && (
        <Tag
          size={size}
          colorScheme="primary"
          variant="subtle"
          borderRadius="full"
        >
          {formatNumberLocal(selectedCount)}
        </Tag>
      )}
    </HStack>
  );
}

/**
 * Deselect any values that don't appear in the list of options.
 *
 * This has to happen in the outer selector component, as the dropdown may not
 * be displaying when the values or options change.
 */
function useClearInvalidValues<T extends string>({
  enabled,
  values,
  optionByValue,
  onChange,
}: {
  enabled: boolean;
  values: T[];
  optionByValue: Map<T, unknown>;
  onChange: (values: T[]) => void;
}) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const validValues = values.filter((value) => optionByValue.has(value));

    if (validValues.length !== values.length) {
      onChange(validValues);
    }
  }, [enabled, optionByValue, values, onChange]);
}
